import { z } from 'zod';

export const recordSchema = z.object({
  id: z.number(),
  g_id: z.string(),
  type: z.enum(['0', '1']), // 0 for buy, 1 for sell
  sym_id: z.number().gte(0, {
    error: 'Symbol is required.',
  }),
  quantity: z.number(),
  share_count: z.number(),
  per_share_price: z.number(),
  commission: z.number(),
  cdc_charges: z.number(),
  sales_tax: z.number(),
  cdc_trans_charges: z.number(),
  capital_gain_tax: z.number().nullable(), // Capital Gain Tax (for sell transactions)
  capital_gain_loss: z.number().nullable(), // Capital Gain Loss (for sell transactions)
  purchase_timestamp: z.iso.datetime(),
  entry_timestamp: z.iso.datetime(),

  // calculated
  totalAmount: z.number(),
  totalAmountBySharesValue: z.number(),
  totalCommissionsAndCharges: z.number(),
  costBasis: z.number().optional(),
  proceeds: z.number().optional(),
});

export type RecordSchema = z.infer<typeof recordSchema>;

export const recordUpdateSchema = recordSchema.omit({
  g_id: true,
  entry_timestamp: true,
  totalCommissionsAndCharges: true,
  totalAmount: true,
  totalAmountBySharesValue: true,
});

export type RecordUpdateSchema = z.infer<typeof recordUpdateSchema>;

export const recordCreateSchema = recordUpdateSchema.omit({
  id: true,
});

export type RecordCreateSchema = z.infer<typeof recordCreateSchema>;

export const recordFormSchema = recordSchema
  .omit({
    g_id: true,
    entry_timestamp: true,
    totalAmount: true,
    totalAmountBySharesValue: true,
    totalCommissionsAndCharges: true,
  })
  .extend({
    type: z.boolean(),
    purchase_timestamp: z.date({
      error: (issue) => (issue.input === undefined ? 'Please provide a valid transaction date.' : undefined),
    }),
    share_count: z
      .number()
      .nullable()
      .refine(
        (v) => {
          const parsed = parseInt(`${v ?? 0}`.toString());
          return !isNaN(parsed) && parsed > 0;
        },
        {
          error: 'Number of Shares must be greater than 0.',
        }
      ),
    per_share_price: z.coerce
      .number<number>()
      .nullable()
      .refine(
        (v) => {
          const parsed = parseFloat(`${v ?? 0}`.toString());
          return !isNaN(parsed) && parsed > 0;
        },
        {
          error: 'Share price must be greater than 0.',
        }
      ),
    commission: z.coerce.number<number>().nullable(),
    cdc_charges: z.coerce.number<number>().nullable(),
    sales_tax: z.coerce.number<number>().nullable(),
    cdc_trans_charges: z.coerce.number<number>().nullable(),
    capital_gain_tax: z.coerce.number<number>().nullable(), // Capital Gain Tax (optional for sell transactions)
    capital_gain_loss: z.coerce.number<number>().nullable(), // Capital Gain Loss (optional for sell transactions)
  });

export type RecordFormSchema = z.infer<typeof recordFormSchema>;
